import { useState } from "react";

import { Delete } from "../../../../svg/delete";

interface HistoryEntryDeleteControlProps {
  onDelete: () => void;
  /** Disables the control and swaps the label while the delete is in flight. */
  isDeleting: boolean;
}

/** Shared styling for the Yes / No links shown once delete is armed. */
const CONFIRM_BUTTON_CLASSES =
  "cursor-pointer border-none bg-transparent p-0 text-tiny font-bold disabled:cursor-not-allowed disabled:opacity-60";

/**
 * Delete affordance for one history entry. Starts as a trash icon and swaps to
 * an inline "Delete? Yes / No" prompt on click, so the confirmation stays
 * inside the card instead of stacking another dialog over the modal it lives in.
 *
 * @param props.onDelete - Runs once the user confirms
 * @param props.isDeleting - True while the Firestore delete is in flight
 */
const HistoryEntryDeleteControl = (props: HistoryEntryDeleteControlProps) => {
  const [isConfirming, setIsConfirming] = useState(false);

  if (!isConfirming) {
    return (
      <button
        type="button"
        className="flex cursor-pointer items-center justify-center border-none bg-transparent p-1 hover:opacity-70"
        onClick={() => setIsConfirming(true)}
        aria-label="Delete history entry"
        title="Delete history entry"
      >
        <Delete height={14} width={14} />
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-tiny text-primaryGray-6">Delete?</span>
      <button
        type="button"
        className={`${CONFIRM_BUTTON_CLASSES} text-red-600`}
        onClick={props.onDelete}
        disabled={props.isDeleting}
      >
        {props.isDeleting ? "Deleting…" : "Yes"}
      </button>
      <button
        type="button"
        className={`${CONFIRM_BUTTON_CLASSES} text-primaryGray-6`}
        onClick={() => setIsConfirming(false)}
        disabled={props.isDeleting}
      >
        No
      </button>
    </div>
  );
};

export default HistoryEntryDeleteControl;
